import { useId, useState } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageProvider';
import { MUSCLE_GROUP_LIST, type MuscleGroupKey } from '@/lib/exerciseLibrary';
import { translateMuscle } from '@/lib/muscleGroupTranslations';
import type { WorkoutExerciseReplacement as ReplacementPayload } from '@/lib/fitness/replacement';
import { workoutReplacementChoices, workoutReplacementGroup, workoutReplacementPayload, type ReplacementEquipment } from '@/lib/fitness/workoutExerciseReplacement';
import { fitnessExerciseReplacementCopy } from '@/lib/fitnessExerciseReplacementCopy';

export interface WorkoutExerciseReplacementProps {
  currentName: string; muscle: string; disabled?: boolean;
  onChoose: (choice: ReplacementPayload) => void;
}
const control = 'min-h-11 rounded-xl border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40';

export default function WorkoutExerciseReplacement(props: WorkoutExerciseReplacementProps) {
  // Changing the current exercise closes the picker and clears the previous filters.
  return <ReplacementPicker key={props.currentName + ':' + props.muscle} {...props} />;
}
function ReplacementPicker({ currentName, muscle, onChoose, disabled = false }: WorkoutExerciseReplacementProps) {
  const { t, lang } = useLanguage();
  const copy = fitnessExerciseReplacementCopy(lang);
  const id = useId();
  const [open, setOpen] = useState(false);
  const [group, setGroup] = useState<MuscleGroupKey | 'all'>(() => workoutReplacementGroup(currentName, muscle) ?? 'all');
  const [equipment, setEquipment] = useState<ReplacementEquipment | 'all'>('all');
  const choices = workoutReplacementChoices(currentName, muscle, group, equipment);
  const equipmentChoices = [...new Set(workoutReplacementChoices(currentName, muscle, group, 'all').map(choice => choice.primaryEquipment))];
  const equipmentLabel = (value: ReplacementEquipment) => ['bodyweight', 'barbell', 'dumbbell', 'cable', 'machine', 'kettlebell', 'ezbar'].includes(value)
    ? t('fitness.equip.' + value) : copy[value as Exclude<ReplacementEquipment, 'bodyweight' | 'barbell' | 'dumbbell' | 'cable' | 'machine' | 'kettlebell' | 'ezbar'>];

  return <div className="border-t border-border pt-2">
    <button type="button" aria-expanded={open} aria-controls={id} disabled={disabled} onClick={() => setOpen(value => !value)} className={control + ' flex items-center gap-2 text-primary'}>
      <ArrowLeftRight size={16} aria-hidden="true" />{copy.replace}
    </button>
    {open && <fieldset id={id} disabled={disabled} className="mt-3 space-y-3">
      <legend className="sr-only">{copy.replace}</legend>
      <p className="text-sm leading-relaxed text-muted-foreground">{copy.description}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1 text-xs text-muted-foreground"><span className="block">{copy.group}</span>
          <select value={group} onChange={event => { setGroup(event.target.value as MuscleGroupKey | 'all'); setEquipment('all'); }} className={control + ' w-full'}>
            <option value="all">{copy.allGroups}</option>
            {MUSCLE_GROUP_LIST.map(value => <option value={value} key={value}>{translateMuscle(value, t)}</option>)}
          </select>
        </label>
        <label className="space-y-1 text-xs text-muted-foreground"><span className="block">{copy.equipment}</span>
          <select value={equipment} onChange={event => setEquipment(event.target.value as ReplacementEquipment | 'all')} className={control + ' w-full'}>
            <option value="all">{copy.allEquipment}</option>
            {equipmentChoices.map(value => <option value={value} key={value}>{equipmentLabel(value)}</option>)}
          </select>
        </label>
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">{copy.reviewSupports}</p>
      {choices.length === 0 ? <p role="status" className="py-2 text-sm text-muted-foreground">{copy.empty}</p> :
        <ul className="max-h-72 space-y-1 overflow-y-auto rounded-xl border border-border p-1">
          {choices.map(choice => <li key={choice.id}>
            <button type="button" onClick={() => { onChoose(workoutReplacementPayload(choice)); setOpen(false); }} className="flex min-h-14 w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40">
              <span className="min-w-0"><span className="block text-sm font-medium text-foreground">{t(choice.name)}</span>
                <span className="mt-1 block text-xs text-muted-foreground">{translateMuscle(choice.muscle, t)} · {equipmentLabel(choice.primaryEquipment)}</span></span>
              <ArrowLeftRight size={16} className="shrink-0 text-primary" aria-hidden="true" />
            </button>
          </li>)}
        </ul>}
    </fieldset>}
  </div>;
}
